import { BaseDataResponse, CredentialCheckResponse } from './../../../../base/entities/BaseEntities';
import { Component, ViewChild } from '@angular/core';
import { DxValidationGroupComponent } from 'devextreme-angular';
import { BaseEntity, BaseState, LoginParams, PasswordRecoveryParams, BaseResponse } from '@base/entities/BaseEntities';
import { BasePageComponent } from '@base/component/BasePageComponent';
import { ServiceBagService, ServiceBagServices } from '@base/component/ServiceBag';
import { EnumNotificationDeliveryOptions, EnumSystemOptions } from '@core/framework/sysenums.enum';
import { SysGlobals } from '@core/framework/sysglobals';
import { RC } from '@core/framework/resources.service';
import { LoginEndpoint } from '@core/endpoints/login.endpoint';
import { LanguageService } from '@m4u-angular/core/framework/language.service';

class State extends BaseState {
    Stage: number;
    UserName: string;
    DeliveryOption: EnumNotificationDeliveryOptions;
    Email: string;
    Phone: string;
    Token: string;
    NewPassword1: string;
    NewPassword2: string;
    UserNameCheckResponse: CredentialCheckResponse;
    DeliveryOptions: any[];
}
@Component({
    providers: ServiceBagServices,
    selector: 'm4u-passwordrecovery',
    templateUrl: './passwordrecovery.component.html',
    styleUrls: ['./passwordrecovery.component.scss']
})
export class PasswordRecoveryComponent extends BasePageComponent<BaseEntity, State>{
    @ViewChild('validationGroup') protected validationGroup: DxValidationGroupComponent;

    constructor(A: ServiceBagService, private languageService: LanguageService, private loginEndpoint: LoginEndpoint) {
        super(A, 'PasswordRecoveryComponent', LoginParams, State);
        this.BindHandler('IsPasswordsMatch');
        this.BindHandler('IsPasswordComplex');
    }

    //Set initial username
    override OnInitState() {
        this.State.Stage = 1;
        this.State.UserName = this.QueryParams.UserName;
        this.State.DeliveryOptions = this.LIST('NotificationDeliveryOptions').filter(x => {
            switch (x.Value) {
                case EnumNotificationDeliveryOptions.Email:
                    return this.SystemOptionFlag(EnumSystemOptions.IsSupportNotificationMethodEmail);
                case EnumNotificationDeliveryOptions.SMS:
                    return this.SystemOptionFlag(EnumSystemOptions.IsSupportNotificationMethodSMS);
                case EnumNotificationDeliveryOptions.WhatsApp:
                    return this.SystemOptionFlag(EnumSystemOptions.IsSupportNotificationMethodWhatsApp);
                default:
                    break;
            }
            return false;
        });
    }

    //Send the recovery code to the phone/email as specified
    //If phone or email are invalid, an error will be returned
    //Upon success, move to the next stage
    OnSendRecoveryCode() {
        if (!this.validationGroup.instance.validate().isValid) return;
        const params = new PasswordRecoveryParams;
        params.UserName = this.State.UserName;
        params.DeliveryOption = this.State.DeliveryOption;
        params.Address = (this.State.DeliveryOption == EnumNotificationDeliveryOptions.Email ? this.State.Email : this.State.Phone);
        params.Language = SysGlobals.Language;
        this.Do(this.loginEndpoint.RecoverPassword(params), false, (response) => {
            if (response.IsSuccess) {
                this.UI.InfoMessage(this.RC('msgNotificationSentTo').replace('{0}', params.Address ));
                this.State.Stage = 3;
            } else {
                this.UI.StopMessage(response.ResponseMessage);
            }
        });
    }

    OnCheckUsername() {
        if (!this.validationGroup.instance.validate().isValid) return;

        const params = new LoginParams();
        params.UserName = this.State.UserName;
        this.Do(this.loginEndpoint.CheckUserName(params), false, (response) => {
            this.State.UserNameCheckResponse = response.Entity;

            if (!this.State.UserNameCheckResponse.IsExists) {
                this.UI.UnexpectedMessage(this.RC('errorUserNameNotFound'));
            } else if (!this.State.UserNameCheckResponse.HasEmail && !this.State.UserNameCheckResponse.HasPhone) {
                this.UI.UnexpectedMessage(this.RC('errorIsNotExistsPhoneAndEmail'));
            } else {
                if (!this.State.UserNameCheckResponse.HasPhone)
                    this.State.DeliveryOptions = this.State.DeliveryOptions.filter(x => x.Value == EnumNotificationDeliveryOptions.Email);
                if (!this.State.UserNameCheckResponse.HasEmail)
                    this.State.DeliveryOptions = this.State.DeliveryOptions.filter(x => x.Value != EnumNotificationDeliveryOptions.Email);
                this.State.Stage = 2;
                this.State.DeliveryOption = this.State.DeliveryOptions?.length > 0 ? this.State.DeliveryOptions[0].Value : null;
            }
        });
    }

    //Verify the recovery token
    //Upon success, move to the next screen
    OnVerifyRecoveryToken() {
        if (!this.validationGroup.instance.validate().isValid) return;
        const params = new PasswordRecoveryParams;
        params.UserName = this.State.UserName;
        params.Token = this.State.Token;
        params.Language = SysGlobals.Language;
        this.Do(this.loginEndpoint.VerifyRecoveryToken(params), false, () => this.State.Stage = 4);
    }

    //Go back to previous stage
    OnResend() {
        this.InitState();
        this.State.Stage = 1;
    }

    OnCancel() {
        this.Navigate(['system/login']);
    }


    IsPasswordsMatch(): boolean {
        return this.State.NewPassword1 == this.State.NewPassword2;
    }

    //Verifies (server-side) whether password is complex enough
    IsPasswordComplex() {
        return new Promise((resolve, reject) => {
            const params = new LoginParams;
            params.SecureKey = this.State.NewPassword1;
            params.Language = SysGlobals.Language;
            this.Do(this.loginEndpoint.VerifyPasswordComplexity(params),
                false, () => resolve(true), (response: BaseResponse) => reject(response.ResponseMessage));
        });
    }

    //Make the change. 
    //After making the change, logout and show the login screen
    //Also, reset the stored password
    OnSubmit() {
        if (!this.validationGroup.instance.validate().isValid) return;
        const params = new PasswordRecoveryParams;
        params.UserName = this.State.UserName;
        params.Token = this.State.Token;
        params.Language = SysGlobals.Language;
        params.NewPassword = this.State.NewPassword1;
        this.Do(this.loginEndpoint.ChangePasswordByToken(params), false, () => {
            this.UI.InfoMessage(RC('titlePasswordChanged'));
            // this.A.Session.LogoutAndResetSession();
            this.A.Session.EndSession(() => {
                this.Navigate(['/system/login'], { Language: SysGlobals.Language });
            });
        });
    }

    GetAdditionalTitle() {
        switch (this.State.Stage) {
            case 1: return this.RC('titleRecoverPassword1');
            case 2: return this.RC('titleRecoverPassword2');
            case 3: return this.RC('titleRecoverPassword3');
            case 4: return this.RC('titleRecoverPassword4');
        }

    }
}
